/**
 * LanguageSwitcherComponent — dropdown compact pour changer de langue.
 *
 * Variants :
 *   - mode="full"    : bouton avec label complet (par défaut)
 *   - mode="compact" : juste le drapeau (pour la navbar mobile / FAB)
 *
 * Utilisable en standalone : ``<app-language-switcher></app-language-switcher>``
 */

import { CommonModule } from '@angular/common';
import { Component, HostListener, Input, computed, inject, signal } from '@angular/core';

import { AVAILABLE_LOCALES, I18nService, LocaleOption } from './i18n.service';

@Component({
  selector: 'app-language-switcher',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="lang-switcher" [class.is-open]="isOpen()" [class.is-compact]="mode === 'compact'">
      <button
        type="button"
        class="lang-trigger"
        (click)="toggle($event)"
        [attr.aria-expanded]="isOpen()"
        aria-haspopup="listbox"
      >
        <span class="lang-flag" aria-hidden="true">{{ current().flag }}</span>
        <span class="lang-label" *ngIf="mode !== 'compact'">{{ current().nativeLabel }}</span>
        <span class="lang-caret material-icons-outlined" aria-hidden="true">expand_more</span>
      </button>

      <ul
        class="lang-menu"
        role="listbox"
        *ngIf="isOpen()"
      >
        <li
          *ngFor="let opt of options"
          role="option"
          [class.is-active]="opt.code === current().code"
        >
          <button
            type="button"
            class="lang-item"
            (click)="choose(opt)"
            [attr.aria-selected]="opt.code === current().code"
          >
            <span class="lang-flag" aria-hidden="true">{{ opt.flag }}</span>
            <span class="lang-item-label">{{ opt.nativeLabel }}</span>
            <span
              *ngIf="opt.code === current().code"
              class="material-icons-outlined lang-check"
              aria-hidden="true"
            >check</span>
          </button>
        </li>
      </ul>
    </div>
  `,
  styles: [`
    :host { display: inline-block; position: relative; }

    .lang-switcher { position: relative; }

    .lang-trigger {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 6px 10px 6px 10px;
      border-radius: var(--vd-radius-md);
      border: 1px solid var(--vd-color-border);
      background: var(--vd-color-surface);
      color: var(--vd-color-text);
      font-size: var(--vd-text-sm);
      font-weight: var(--vd-weight-medium);
      cursor: pointer;
      transition: all var(--vd-duration-fast) ease;
    }
    .lang-trigger:hover {
      border-color: var(--vd-primary-300);
      color: var(--vd-primary-600);
    }
    .is-open .lang-trigger {
      border-color: var(--vd-primary-500);
      color: var(--vd-primary-600);
    }
    .is-compact .lang-trigger {
      padding: 6px;
      width: 36px;
      height: 36px;
      justify-content: center;
      border-radius: 999px;
    }
    .is-compact .lang-caret { display: none; }

    .lang-flag { font-size: 18px; line-height: 1; }
    .lang-label { font-variant-numeric: tabular-nums; }
    .lang-caret { font-size: 18px !important; opacity: 0.7; }

    .lang-menu {
      position: absolute;
      top: calc(100% + 6px);
      right: 0;
      list-style: none;
      padding: 4px;
      margin: 0;
      background: var(--vd-color-surface);
      border: 1px solid var(--vd-color-border-strong);
      border-radius: var(--vd-radius-md);
      box-shadow: var(--vd-shadow-md);
      min-width: 180px;
      z-index: 1000;
    }
    :host-context([dir="rtl"]) .lang-menu,
    :host-context(.vd-rtl) .lang-menu { right: auto; left: 0; }

    .lang-menu li { padding: 0; }
    .lang-item {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      background: transparent;
      border: 0;
      border-radius: var(--vd-radius-sm);
      color: var(--vd-color-text);
      font-size: var(--vd-text-sm);
      font-weight: var(--vd-weight-medium);
      cursor: pointer;
      text-align: start;
      transition: background var(--vd-duration-fast) ease;
    }
    .lang-item:hover { background: var(--vd-color-surface-2); }
    .lang-item-label { flex: 1 1 auto; }
    .lang-check { font-size: 18px !important; color: var(--vd-primary-600); }
    .lang-menu li.is-active .lang-item { color: var(--vd-primary-600); }
  `],
})
export class LanguageSwitcherComponent {
  private i18n = inject(I18nService);

  @Input() mode: 'full' | 'compact' = 'full';

  readonly options: LocaleOption[] = AVAILABLE_LOCALES;
  readonly current = this.i18n.currentOption;
  readonly isOpen = signal<boolean>(false);

  toggle(ev: Event): void {
    ev.stopPropagation();
    this.isOpen.update((v) => !v);
  }

  choose(opt: LocaleOption): void {
    this.i18n.setLocale(opt.code);
    this.isOpen.set(false);
  }

  @HostListener('document:click')
  onDocClick(): void {
    if (this.isOpen()) this.isOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.isOpen()) this.isOpen.set(false);
  }
}
